import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import { Paginator } from 'primereact/paginator'
import Badge from '../../../components/ui/Badge'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import FilterBar from '../../../components/ui/FilterBar'
import { useToast } from '../../../components/ui/ToastProvider'
import { formatDate } from '../../../lib/datetime'
import { searchTerm } from '../../../lib/searchTerm'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { useDeadlineRequests } from '../hooks/useDeadlineRequests'
import {
  DEADLINE_REQUEST_STATUS,
  DEADLINE_REQUEST_TAB,
  type DeadlineRequest,
  type DeadlineRequestTab,
} from '../types'
import ApproveDialog from './ApproveDialog'
import { deadlineRequestColumns, requestStatusTone } from './DeadlineRequestColumns'
import DeadlineRequestRowActions from './DeadlineRequestRowActions'
import RejectDialog from './RejectDialog'
import '../deadlineRequests.css'

/** Varsayılan sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/** Arama debounce süresi (anayasa Page Standards ~400 ms). */
const DEBOUNCE_MS = 400

/**
 * Durum filtresindeki "Tümü" sentinel'i. Boş-OLMAYAN bir değer gerekir: PrimeReact Dropdown,
 * `null`/`''` değerli seçeneği "boş seçim" sayıp `onChange`'i yutar (078/045'te belgelenmiştir).
 * Negatif seçildi çünkü `REFERANS.ID` daima pozitiftir.
 */
const ALL_STATUSES = -1

/**
 * Sekmeye göre **varsayılan** durum filtresi (kullanıcı kararı 2026-08-14):
 *
 * - **Taleplerim** → filtresiz; kullanıcı kendi taleplerinin tamamını (sonuçlananlar dahil) görür.
 * - **Onay Bekleyenler** → **Beklemede**; sekme bir iş kuyruğudur, açılışta yapılacak işi göstermeli.
 *
 * Filtre kilitli değildir — combodan "Tüm Durumlar" seçilerek geçmiş kararlar da görülebilir.
 */
function defaultStatusFor(tab: DeadlineRequestTab): number {
  return tab === DEADLINE_REQUEST_TAB.INBOX ? DEADLINE_REQUEST_STATUS.PENDING : ALL_STATUSES
}

interface Props {
  /** Sunucudaki kapsam daraltması: kendi talepleri (`MINE`) ya da onay kuyruğu (`INBOX`). */
  tab: DeadlineRequestTab
}

/**
 * Hedef teslim tarihi güncelleme taleplerinin **liste gövdesi** (087/US4): arama + durum süzgeci,
 * tablo (dar ekranda kart listesi), sayfalama ve Onayla/Reddet pop-up'ları.
 *
 * <p><b>Neden ayrı bileşen</b> (131): aynı onay kuyruğu iki ekranda gösterilir — "Hedef Teslim Tarihi
 * Güncelleme Talebi" ekranının "Onay Bekleyenler" sekmesi ve Onaylarım ekranının talep sekmesi.
 * 131 FR-012 iki yolun **birebir aynı** davranmasını ister; gövde kopyalansaydı bir sonraki 087
 * düzeltmesi yalnız birine yapılırdı.
 *
 * <p><b>Durum sekmeye bağlıdır ve sekme değişince sıfırlanmalıdır</b>: iki sekmenin varsayılan durum
 * filtresi farklıdır; taşınan bir filtre yeni sekmede sessizce yanlış kapsam gösterirdi. Çağıran bu
 * yüzden paneli `key={tab}` ile kurar — sekme değişiminde panel yeniden kurulur ve arama, durum ve sayfa
 * kendiliğinden ilk hâline döner.
 *
 * <p><b>Görünürlük sunucuda daraltılır</b> (FR-011/FR-012). <b>Onayla/Reddet</b> yalnız sunucunun
 * `canDecide` işaretlediği satırlarda ve yalnız onay kuyruğunda çıkar (FR-014); onayda talep edilen
 * tarih dolu gelir, rette açıklama zorunludur.
 */
export default function DeadlineRequestsPanel({ tab }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  /**
   * Durum filtresinin **kullanıcı seçimi**; `null` → "seçilmedi", filtre sekmenin varsayılanıdır
   * ({@link defaultStatusFor}). `null` sentinel'i {@link ALL_STATUSES} ile karışmaz: "Tüm Durumlar" da
   * bir **seçimdir** (−1) ve seçilmemiş hâlden ayrı durur.
   */
  const [statusPick, setStatusPick] = useState<number | null>(null)
  const statusId = statusPick ?? defaultStatusFor(tab)
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)

  /** Onay/ret pop-up'ının konusu; `null` → kapalı. */
  const [approving, setApproving] = useState<DeadlineRequest | null>(null)
  const [rejecting, setRejecting] = useState<DeadlineRequest | null>(null)

  /** İşlem süren satır — yalnız o satırın butonları kilitlenir (078-b deseni). */
  const [busyId, setBusyId] = useState<number | null>(null)

  /**
   * Arama debounce'u + sayfa sıfırlama. `mounted` bayrağı ilk çalıştırmayı atlar: efekt mount'ta da
   * koşar ve 400 ms sonra `setFirst(0)` yapardı — kullanıcı o aralıkta 2. sayfaya geçtiyse sayfa
   * kendiliğinden 1'e dönerdi (078'de belgelenen tuzak).
   */
  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    const id = setTimeout(() => {
      setDebounced(search)
      setFirst(0)
    }, DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  const { list, statuses, approve, reject } = useDeadlineRequests({
    tab,
    q: searchTerm(debounced),
    statusId: statusId === ALL_STATUSES ? null : statusId,
    page: Math.floor(first / rows),
    size: rows,
  })

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  // Dar ekranda (eşik: CARD_VIEW_BREAKPOINT — lib/useMediaQuery) 9-10 kolonlu tablo yerine kart listesi — birincil alanlar öne çıkar
  // (aksiyon + termin değişimi + durum), ikincil alanlar (talep eden/tarihleri/notlar) küçük/silik
  // durur ama KALDIRILMAZ (bkz. ui.css `.ui-card-list`).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  /**
   * Durum seçenekleri **sunucudan** gelir — `REFERANS(DEADLINE_UPDATE_REQUEST_STATUS)`, dile göre
   * çözülmüş adlarla. Sabit üçlü liste koda gömülmez (`ActionsPage`/`SentEmailsAdminPage` deseni):
   * kod tablosu analistindir ve orada ad değişirse combo kendiliğinden değişir.
   *
   * "Tümü" seçeneği istemcide, listenin **başına** eklenir — o bir katalog kaydı değil, filtreyi
   * kaldıran bir seçimdir.
   */
  const statusOptions = useMemo(
    () => [
      { label: t('deadlineRequests.filter.statusAll'), value: ALL_STATUSES },
      ...(statuses.data ?? []).map((s) => ({ label: s.name, value: s.id })),
    ],
    [statuses.data, t],
  )

  /**
   * "Filtre var mı" ölçüsü sekmenin **varsayılanına** göredir, `ALL_STATUSES`'a göre değil: "Onay
   * Bekleyenler" açılışta Beklemede ile gelir ve bu bir kullanıcı filtresi sayılmaz — sayılsa ekran
   * daha ilk açılışta "Temizle" gösterir ve boş kuyrukta "filtreye uyan talep bulunamadı" derdi.
   */
  const hasFilter = searchTerm(search) !== '' || statusId !== defaultStatusFor(tab)

  const clearAll = () => {
    setSearch('')
    // `debounced`'ı da hemen boşalt: aksi halde 400 ms boyunca eski arama sunucuya gitmeye devam eder.
    setDebounced('')
    setStatusPick(null)
    setFirst(0)
  }

  /**
   * Onay. Hata toast'ını merkezî hata yönetimi (019) basar; ekran yalnız satır kilidini çözer.
   *
   * <p><b>Hata yolunda da pop-up kapatılır ve liste tazelenir.</b> Aksi halde 409
   * (`OPTIMISTIC_LOCK_FAILURE` — başka biri aynı talebi işledi) sonrası pop-up **bayat `modDate` ile
   * açık kalıyor** ve her yeniden deneme aynı hatayı veriyordu: kullanıcı pop-up'ı elle kapatıp
   * yeniden açmak zorunda kalırdı (bağımsız code-review bulgusu).
   */
  const onApproveSubmit = (approvedDeadline: string) => {
    if (!approving) return
    setBusyId(approving.id)
    approve.mutate(
      { id: approving.id, body: { approvedDeadline, modDate: approving.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('deadlineRequests.toast.approved'),
            life: 3000,
          })
        },
        onSettled: () => {
          setApproving(null)
          setBusyId(null)
        },
      },
    )
  }

  /** Ret — {@link onApproveSubmit} ile aynı hata/kapatma davranışı. */
  const onRejectSubmit = (rejectNote: string) => {
    if (!rejecting) return
    setBusyId(rejecting.id)
    reject.mutate(
      { id: rejecting.id, body: { rejectNote, modDate: rejecting.modDate } },
      {
        onSuccess: () => {
          toast.show({
            severity: 'success',
            summary: t('deadlineRequests.toast.rejected'),
            life: 3000,
          })
        },
        onSettled: () => {
          setRejecting(null)
          setBusyId(null)
        },
      },
    )
  }

  /**
   * Boş durum metni sekmeye göre değişir: "Taleplerim" boşsa kullanıcıya nasıl talep gireceği anlatılır,
   * "Onay Bekleyenler" boşsa yapacak iş olmadığı söylenir. Anahtarlar **düz literal** tutulur (şablon
   * dizgiyle kurulan anahtar tip denetiminden kaçar ve yazım hatası çalışma zamanına kalır).
   */
  const emptyKeys =
    tab === DEADLINE_REQUEST_TAB.INBOX
      ? {
          title: 'deadlineRequests.empty.inbox.title',
          description: 'deadlineRequests.empty.inbox.description',
        }
      : {
          title: 'deadlineRequests.empty.mine.title',
          description: 'deadlineRequests.empty.mine.description',
        }

  return (
    <>
      <ApproveDialog
        request={approving}
        submitting={approve.isPending}
        onSubmit={onApproveSubmit}
        onHide={() => setApproving(null)}
      />
      <RejectDialog
        request={rejecting}
        submitting={reject.isPending}
        onSubmit={onRejectSubmit}
        onHide={() => setRejecting(null)}
      />

      {/* `--wide-search`: uzun placeholder ("...ile ara") 210px tabanda kirpiliyordu
          (kullanıcı isteği 2026-08-27). */}
      <FilterBar
        className="ui-filterbar--wide-search"
        searchValue={search}
        searchPlaceholder={t('deadlineRequests.filter.searchPlaceholder')}
        searchAriaLabel={t('deadlineRequests.filter.searchPlaceholder')}
        onSearch={setSearch}
        showClear={hasFilter}
        clearLabel={t('deadlineRequests.filter.clear')}
        onClear={clearAll}
      >
        <Dropdown
          value={statusId}
          options={statusOptions}
          onChange={(e) => {
            setStatusPick((e.value as number | null) ?? ALL_STATUSES)
            setFirst(0)
          }}
          aria-label={t('deadlineRequests.filter.statusLabel')}
          disabled={statuses.isError}
        />
      </FilterBar>

      {/* Sunucu-taraflı sayfalama (lazy). Kolon sıralaması kapalı → sortField/onSort verilmez. */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              icon="pi pi-calendar"
              title={hasFilter ? t('deadlineRequests.emptyFiltered.title') : t(emptyKeys.title)}
              description={
                hasFilter
                  ? t('deadlineRequests.emptyFiltered.description')
                  : t(emptyKeys.description)
              }
            />
          )}
          {!list.isLoading &&
            items.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-head">
                  <Badge tone={requestStatusTone(row.statusId)}>{row.statusName ?? ''}</Badge>
                  {tab === DEADLINE_REQUEST_TAB.INBOX && row.canDecide && (
                    <DeadlineRequestRowActions
                      row={row}
                      onApprove={setApproving}
                      onReject={setRejecting}
                      busy={busyId === row.id}
                    />
                  )}
                </div>
                <span className="ui-card-title" title={row.actionName ?? undefined}>
                  {row.actionName}
                </span>
                <div className="ui-card-row">
                  {row.currentDeadline && <span>{formatDate(row.currentDeadline)}</span>}
                  <i className="pi pi-arrow-right" style={{ fontSize: 10 }} aria-hidden />
                  {row.requestedDate && (
                    <span className="dr-date-new">{formatDate(row.requestedDate)}</span>
                  )}
                </div>
                {row.requestNote && (
                  <span className="ui-card-sub" title={row.requestNote}>
                    {row.requestNote}
                  </span>
                )}
                {row.rejectNote && (
                  <span className="ui-card-sub" title={row.rejectNote}>
                    {row.rejectNote}
                  </span>
                )}
                <div className="ui-card-row">
                  {row.requestUserName && (
                    <span style={{ alignItems: 'center' }}>{row.requestUserName}</span>
                  )}
                  {row.creDate && <span>{formatDate(row.creDate)}</span>}
                  {row.approveOrRejectTime && <span>{formatDate(row.approveOrRejectTime)}</span>}
                </div>
              </div>
            ))}
          {!list.isLoading && total > rows && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              onPageChange={(e) => {
                setFirst(e.first)
                setRows(e.rows)
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="ui-cards-paginator"
            />
          )}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={list.isLoading}
          lazy
          first={first}
          rows={rows}
          totalRecords={total}
          onPage={(e) => {
            setFirst(e.first)
            setRows(e.rows)
          }}
          emptyMessage={
            <EmptyState
              icon="pi pi-calendar"
              title={hasFilter ? t('deadlineRequests.emptyFiltered.title') : t(emptyKeys.title)}
              description={
                hasFilter
                  ? t('deadlineRequests.emptyFiltered.description')
                  : t(emptyKeys.description)
              }
            />
          }
        >
          {deadlineRequestColumns({
            t,
            onApprove: setApproving,
            onReject: setRejecting,
            busyId,
            showActions: tab === DEADLINE_REQUEST_TAB.INBOX,
          })}
        </DataTable>
      )}
    </>
  )
}
